import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
	ChecklistFormType,
	ChecklistItemMaxOptions,
	ChecklistPriority,
	ChecklistType,
	QuizQuestionStatus,
} from '../../globals';
import { IChecklist, ChecklistOption, IChecklistData } from "../../interfaces/checklistItem";
import {assert} from '../../classes/utilities/ErrorHandling';
import {makeChecklistItemData} from "../../classes/Checklist";

// Generators
function* idGenerator()
{
	for (let i = 0; ; i++)
	{
		yield i;
	}
}

function generateNextOptionName(optionsList: ChecklistOption[], startingNumber = 0): string
{
	for (const option of optionsList)
	{
		if (
			option.description.toLowerCase() ===
			`answer ${String.fromCharCode(65 + startingNumber).toLowerCase()}`
		)
		{
			return generateNextOptionName(optionsList, startingNumber + 1);
		}
	}
	return `Answer ${String.fromCharCode(65 + startingNumber)}`;
}

const uniqueIdGenerator = idGenerator();

// Interfaces for state
type QuizAnswerValue = boolean | null;

export interface IChecklistState {
	checklist: IChecklistData;
	answers: Record<string, Array<QuizAnswerValue>>;
	attempts: Record<string, number>;
	quizSettings: Record<string, any>;
	questionStatus: QuizQuestionStatus;
	simulationEvent: string | number;
	preview: boolean;
	nextStep: string;
	clipboard: IChecklist | null;
	currentNumberOfRetries: number;
}

// Initial state
const initialState: IChecklistState = {
	checklist: {},
	answers: {},
	attempts: {},
	quizSettings: {},
	questionStatus: QuizQuestionStatus.UNANSWERED,
	simulationEvent: '',
	preview: false,
	nextStep: '',
	clipboard: null,
	currentNumberOfRetries: 0,
};

// Action payload interfaces
export interface SetDescriptionAction {
	stepId: string;
	type: ChecklistFormType;
	item: number;
	description: string;
}

export interface SetPrefillAction {
	stepId: string;
	item: number;
	prefill: string;
}

export interface SetHighlightSphereDiameterAction {
	stepId: string;
	type: ChecklistFormType;
	item: number;
	value: number;
}

export interface AddItemAction
{
	stepId: string;
	type: ChecklistFormType;
	itemData?: any;
}

// The improved ChecklistSlice
const ChecklistSlice = createSlice({
	name: "checklist",
	initialState,
	reducers: {
		loadChecklist(state, action: PayloadAction<IChecklistData>)
		{
			if (action.payload)
			{
				for (const [stepId, data] of Object.entries(action.payload))
				{
					if (!data || Object.keys(data).length <= 0)
					{
						delete action.payload[stepId];
						continue;
					}

					if (Array.isArray(data))
					{
						// Convert to new structure
						const oldData = [...data];
						let newBehaviour = ChecklistPriority.PASSIVE;
						for (const oldDataItem of oldData)
						{
							if (
								newBehaviour === ChecklistPriority.PASSIVE &&
								oldDataItem.priority !== ChecklistPriority.PASSIVE
							)
							{
								newBehaviour = ChecklistPriority.FORCEVIEW;
							}
							if (oldDataItem.priority === 'must-respond')
							{
								oldDataItem.mustRespond = true;
							}
							delete oldDataItem.priority;
							delete oldDataItem.event;
						}
						action.payload[stepId] = {
							type: ChecklistFormType.FORM,
							behaviour: newBehaviour,
							items: [...oldData],
							questions: [],
							next: '',
						};
					}

					if (!action.payload[stepId].type)
					{
						console.log('setting checklist type to form');
						action.payload[stepId].type = ChecklistFormType.NONE;
					}

					if (!action.payload[stepId].next)
					{
						action.payload[stepId].next = '';
					}

					for (const item of action.payload[stepId].items)
					{
						if (item.mustRespond === undefined) item.mustRespond = false;
						if (item.prefill === undefined) item.prefill = '';

						item.id = uniqueIdGenerator.next().value as number;

						if (item.next && !action.payload[stepId].next)
						{
							action.payload[stepId].next = item.next;
						}

						delete item.next;

						if (item.options && item.options.length > 0)
						{
							for (const option of item.options)
							{
								option.id = uniqueIdGenerator.next().value as number;
							}
						}
						else if (item.type === ChecklistType.YESNO)
						{
							// Convert YESNO to MULTI_OPTION here
							item.type = ChecklistType.MULTI_OPTION;
							for (const option of item.options)
							{
								option.id = uniqueIdGenerator.next().value as number;
							}
						}
					}

					if (action.payload[stepId].questions)
					{
						for (const question of action.payload[stepId].questions)
						{
							question.id = uniqueIdGenerator.next().value as number;

							if (question.mustRespond === undefined)
							{
								question.mustRespond = true;
							}

							if (question.next && !action.payload[stepId].next)
							{
								action.payload[stepId].next = question.next;
							}

							if (!question.highlightColor)
							{
								question.highlightColor = '#E2A42E';
							}

							if (!question.highlightSphereDiameter)
							{
								question.highlightSphereDiameter = 0.05;
							}

							delete question.next;

							if (question.options && question.options.length > 0)
							{
								for (const option of question.options)
								{
									option.id = uniqueIdGenerator.next().value as number;
								}
							}
						}
					}
				}
			}

			state.checklist = action.payload ?? {};
		},
		setDescription(state, action: PayloadAction<SetDescriptionAction>)
		{
			const { stepId, type, item, description } = action.payload;
			switch (type)
			{
				case ChecklistFormType.FORM:
					state.checklist[stepId].items[item].description = description;
					return;
				case ChecklistFormType.QUIZ:
					state.checklist[stepId].questions[item].description = description;
					return;
			}
		},
		setPrefill(state, action: PayloadAction<SetPrefillAction>)
		{
			const { stepId, item, prefill } = action.payload;
			state.checklist[stepId].items[item].prefill = prefill;
		},
		setHighlightSphereDiameter(
			state,
			action: PayloadAction<SetHighlightSphereDiameterAction>
		)
		{
			const { stepId, type, item, value } = action.payload;
			switch (type)
			{
				case ChecklistFormType.FORM:
					state.checklist[stepId].items[item].highlightSphereDiameter = value;
					return;
				case ChecklistFormType.QUIZ:
					state.checklist[stepId].questions[item].highlightSphereDiameter = value;
					return;
			}
		},
		setAttempts(state, action)
		{
			state.checklist[action.payload.stepId].questions[action.payload.item].attempts = action.payload.attempts;
		},
		setQuestionNext(state, action)
		{
			state.checklist[action.payload.stepId].next = action.payload.next;
		},
		setCorrectAnswer(state, action)
		{
			const questionType = state.checklist[action.payload.stepId].questions[action.payload.item].type;
			//clear all correct answers if only 1 is allowed and the incoming 1 is true
			if (action.payload.correct === true && (questionType === ChecklistType.MULTI_OPTION || questionType === ChecklistType.MULTI_OPTION_3D))
			{
				for (let i = 0; i < state.checklist[action.payload.stepId].questions[action.payload.item].options.length; i++)
				{
					state.checklist[action.payload.stepId].questions[action.payload.item].options[i].correct = false;
				}
			}

			if (action.payload.correct !== true)
			{ //make sure we have at least 1 correct answer if we uncheck this one
				let correctAnswerCount = 0;
				for (let i = 0; i < state.checklist[action.payload.stepId].questions[action.payload.item].options.length; i++)
				{
					if (state.checklist[action.payload.stepId].questions[action.payload.item].options[i].correct === true)
					{correctAnswerCount++;}
				}

				if (correctAnswerCount <= 1) return;
			}

			state.checklist[action.payload.stepId].questions[action.payload.item].options[action.payload.index].correct = action.payload.correct;
		},
		setAnswerObject(state, action)
		{
			console.log('[ChecklistSlice] Setting answer object:', {
				stepId: action.payload.stepId,
				item: action.payload.item,
				index: action.payload.index,
				object: action.payload.object
			});

			const previousObject = state.checklist[action.payload.stepId].questions[action.payload.item].options[action.payload.index].object;
			if (previousObject)
			{
				console.log('[ChecklistSlice] Replacing previous object:', previousObject);
			}

			state.checklist[action.payload.stepId].questions[action.payload.item].options[action.payload.index].object = action.payload.object;

			console.log('[ChecklistSlice] Answer object set successfully');
		},
		toggleMustRespond(state, action)
		{
			switch (action.payload.type)
			{
				case ChecklistFormType.QUIZ:
					state.checklist[action.payload.stepId].questions[action.payload.item].mustRespond = !state.checklist[action.payload.stepId].questions[action.payload.item].mustRespond;
					return;
				default:
					state.checklist[action.payload.stepId].items[action.payload.item].mustRespond = !state.checklist[action.payload.stepId].items[action.payload.item].mustRespond;
					return;
			}

		},
		toggleAllowMultiplePhotos(state, action)
		{
			switch (action.payload.type)
			{
				case ChecklistFormType.QUIZ:
					state.checklist[action.payload.stepId].questions[action.payload.item].allowMultiplePhotos = !state.checklist[action.payload.stepId].questions[action.payload.item].allowMultiplePhotos;
					return;
				default:
					state.checklist[action.payload.stepId].items[action.payload.item].allowMultiplePhotos = !state.checklist[action.payload.stepId].items[action.payload.item].allowMultiplePhotos;
					return;
			}

		},
		toggleShowCallouts(state, action)
		{
			state.checklist[action.payload.stepId].questions[action.payload.item].showCallouts = action.payload.value;
		},
		toggleShowHighlights(state, action)
		{
			state.checklist[action.payload.stepId].questions[action.payload.item].showHighlights = action.payload.value;
		},
		setHighlightColor(state, action)
		{
			state.checklist[action.payload.stepId].questions[action.payload.item].highlightColor = action.payload.value;
		},
		setGradedQuestionScore(state, action)
		{
			state.checklist[action.payload.stepId].questions[action.payload.item].score = action.payload.score;
		},
		setBehaviour(state, action)
		{
			if (!state.checklist[action.payload.stepId])
			{
				state.checklist[action.payload.stepId] = { type: ChecklistFormType.FORM, items: [], questions: [], behaviour: action.payload.behaviour, next: "" };
			}

			state.checklist[action.payload.stepId].behaviour = action.payload.behaviour;
		},
		setFormType(state, action)
		{
			if (!state.checklist[action.payload.stepId])
			{
				state.checklist[action.payload.stepId] = { behaviour: ChecklistPriority.AUTOOPEN, items: [], questions: [], next: "", type: action.payload.type };
			}

			state.checklist[action.payload.stepId].type = action.payload.type;

			if (action.payload.type === ChecklistFormType.QUIZ && (!state.checklist[action.payload.stepId].questions || state.checklist[action.payload.stepId].questions.length === 0))
			{
				state.checklist[action.payload.stepId].questions = [];
				state.checklist[action.payload.stepId].questions.push(
					{
						type: ChecklistType.MULTI_OPTION,
						id: uniqueIdGenerator.next().value!,
						description: "New Question",
						mustRespond: true,
						attempts: 1,
						highlightSphereDiameter: 0.05,
						score: 0,
						showCallouts: true,
						showHighlights: true,
						highlightColor: "#E2A42E",
						next: "",
						options: makeChecklistItemData(ChecklistType.MULTI_OPTION)!,
					}
				);

				const tempOptions = new Array<any>();

				for (const tempOption of state.checklist[action.payload.stepId].questions[0].options)
				{
					tempOptions.push({ ...tempOption, id: uniqueIdGenerator.next().value, correct: false, object: null });
				}
				state.checklist[action.payload.stepId].questions[0].options = [...tempOptions];
				state.checklist[action.payload.stepId].questions[0].options[0].correct = true;
			}
		},
		setType(state, action)
		{
			switch (action.payload.formType)
			{
				case ChecklistFormType.FORM:
					state.checklist[action.payload.stepId].items[action.payload.item].type = action.payload.type;
					state.checklist[action.payload.stepId].items[action.payload.item].options = action.payload.options ?? makeChecklistItemData(action.payload.type);

					if (action.payload.type === ChecklistType.MULTI_OPTION || action.payload.type === ChecklistType.MULTI_OPTION_SELECT)
					{
						const tempOptions = new Array<any>();
						for (const tempOption of state.checklist[action.payload.stepId].items[action.payload.item].options)
						{
							tempOptions.push({ ...tempOption, id: uniqueIdGenerator.next().value });
						}
						state.checklist[action.payload.stepId].items[action.payload.item].options = [...tempOptions];
					}
					return;
				case ChecklistFormType.QUIZ:
					state.checklist[action.payload.stepId].questions[action.payload.item].type = action.payload.type;

					if (!state.checklist[action.payload.stepId].questions[action.payload.item].attempts)
					{
						state.checklist[action.payload.stepId].questions[action.payload.item].attempts = 1;
					}

					if(!state.checklist[action.payload.stepId].questions[action.payload.item].showCallouts)
					{
						state.checklist[action.payload.stepId].questions[action.payload.item].showCallouts = true;
					}

					if(!state.checklist[action.payload.stepId].questions[action.payload.item].showHighlights)
					{
						state.checklist[action.payload.stepId].questions[action.payload.item].showHighlights = true;
					}

					if(!state.checklist[action.payload.stepId].questions[action.payload.item].highlightColor)
					{
						state.checklist[action.payload.stepId].questions[action.payload.item].highlightColor = "#E2A42E";
					}

					if(!state.checklist[action.payload.stepId].questions[action.payload.item].highlightSphereDiameter)
					{
						state.checklist[action.payload.stepId].questions[action.payload.item].highlightSphereDiameter = 0.05;
					}

					if(!state.checklist[action.payload.stepId].next)
					{
						state.checklist[action.payload.stepId].next = "";
					}

					//if its a new type aka previous question/answer didnt exist make a new one
					if (!state.checklist[action.payload.stepId].questions[action.payload.item].options || state.checklist[action.payload.stepId].questions[action.payload.item].options.length <= 0)
					{
						state.checklist[action.payload.stepId].questions[action.payload.item].options = action.payload.options ?? makeChecklistItemData(action.payload.type);
						if (action.payload.type === ChecklistType.MULTI_OPTION ||
							action.payload.type === ChecklistType.MULTI_OPTION_SELECT ||
							action.payload.type === ChecklistType.MULTI_OPTION_3D ||
							action.payload.type === ChecklistType.MULTI_OPTION_SELECT_3D )
						{
							const tempOptions = new Array<any>();
							for (const tempOption of state.checklist[action.payload.stepId].questions[action.payload.item].options)
							{
								tempOptions.push({ ...tempOption, id: uniqueIdGenerator.next().value, correct: false, object: null });
							}
							state.checklist[action.payload.stepId].questions[action.payload.item].options = [...tempOptions];
							state.checklist[action.payload.stepId].questions[action.payload.item].options[0].correct = true;
						}
					}
					else
					{ //if previous question and answers existed do nothing and then make sure to remove illegal radio answers
						if (action.payload.type === ChecklistType.MULTI_OPTION || action.payload.type === ChecklistType.MULTI_OPTION_3D)
						{
							for (let i = 0; i < state.checklist[action.payload.stepId].questions[action.payload.item].options.length; i++)
							{
								if (state.checklist[action.payload.stepId].questions[action.payload.item].options[i].correct)
								{
									for (let j = 0; j < state.checklist[action.payload.stepId].questions[action.payload.item].options.length; j++)
									{
										state.checklist[action.payload.stepId].questions[action.payload.item].options[j].correct = false;
									}
									state.checklist[action.payload.stepId].questions[action.payload.item].options[i].correct = true;
									break;
								}
							}
						}
					}
					return;
			}
		},
		moveItem(state, action)
		{
			switch (action.payload.type)
			{
				case ChecklistFormType.FORM:
					const newArray = state.checklist[action.payload.stepId].items.filter((item, idx) => idx !== action.payload.item);
					newArray.splice(action.payload.index, 0, { ...state.checklist[action.payload.stepId].items[action.payload.item] });

					state.checklist[action.payload.stepId].items = newArray;
					return;
				case ChecklistFormType.QUIZ:
					const newArrayQuiz = state.checklist[action.payload.stepId].questions.filter((item, idx) => idx !== action.payload.item);
					newArrayQuiz.splice(action.payload.index, 0, { ...state.checklist[action.payload.stepId].questions[action.payload.item] });

					state.checklist[action.payload.stepId].questions = newArrayQuiz;
					return;
			}
		},
		addItem(state, action: PayloadAction<AddItemAction>)
		{
			console.log('[ChecklistSlice] addItem reducer called with:', {
				stepId: action.payload.stepId,
				type: action.payload.type,
				itemData: action.payload.itemData ? {
					type: action.payload.itemData.type,
					description: action.payload.itemData.description,
					options: action.payload.itemData.options?.map(opt => ({
						description: opt.description,
						correct: opt.correct
					}))
				} : null
			});

			if (!state.checklist[action.payload.stepId])
			{
				console.log('[ChecklistSlice] Creating new checklist for step:', action.payload.stepId);
				state.checklist[action.payload.stepId] = { behaviour: ChecklistPriority.PASSIVE, items: [], questions: [], next: "", type: action.payload.type };
			}

			if (!state.checklist[action.payload.stepId].questions)
			{
				console.log('[ChecklistSlice] Initializing questions array for step:', action.payload.stepId);
				state.checklist[action.payload.stepId].questions = [];
			}

			switch (action.payload.type)
			{
				case ChecklistFormType.FORM:
					console.log('[ChecklistSlice] Adding form item to step:', action.payload.stepId);
					state.checklist[action.payload.stepId].items.push(
						action.payload.itemData ?? {
							type: ChecklistType.TEXT,
							id: uniqueIdGenerator.next().value,
							description: "New Question",
							mustRespond: false,
							prefill: '',
						}
					);
					return;
				case ChecklistFormType.QUIZ:
					console.log('[ChecklistSlice] Adding quiz item to step:', action.payload.stepId);
					console.log('[ChecklistSlice] Current questions count:', state.checklist[action.payload.stepId].questions.length);
					console.log('[ChecklistSlice] Quiz item data:', action.payload.itemData ? {
						type: action.payload.itemData.type,
						description: action.payload.itemData.description,
						options: action.payload.itemData.options?.map(opt => ({
							description: opt.description,
							correct: opt.correct
						}))
					} : 'No item data provided');

					state.checklist[action.payload.stepId].questions.push(
						action.payload.itemData ?? {
							type: ChecklistType.MULTI_OPTION,
							id: uniqueIdGenerator.next().value,
							description: "New Question",
							mustRespond: true,
							attempts: 1,
							highlightSphereDiameter: 0.05,
							score: 0,
							showCallouts: true,
							showHighlights: true,
							highlightColor: "#E2A42E",
							options: makeChecklistItemData(ChecklistType.MULTI_OPTION),
						}
					);

					const questionIndex = state.checklist[action.payload.stepId].questions.length - 1;
					console.log('[ChecklistSlice] Added question at index:', questionIndex);

					if (!action.payload.itemData)
					{
						console.log('[ChecklistSlice] Setting up default options for question');
						const tempOptions = new Array<any>();

						for (const tempOption of state.checklist[action.payload.stepId].questions[questionIndex].options)
						{
							tempOptions.push({ ...tempOption, id: uniqueIdGenerator.next().value, correct: false, object: null });
						}
						state.checklist[action.payload.stepId].questions[questionIndex].options = [...tempOptions];
						state.checklist[action.payload.stepId].questions[questionIndex].options[0].correct = true;
						console.log('[ChecklistSlice] Default options set:', tempOptions.map(opt => ({
							description: opt.description,
							correct: opt.correct
						})));
					}
					console.log('[ChecklistSlice] Quiz item addition completed');
					return;
			}
		},
		removeItem(state, action)
		{
			switch (action.payload.type)
			{
				case ChecklistFormType.FORM:
					state.checklist[action.payload.stepId].items.splice(action.payload.item, 1);
					return;
				case ChecklistFormType.QUIZ:
					state.checklist[action.payload.stepId].questions.splice(action.payload.item, 1);
					return;
			}
		},
		setOption(state, action)
		{
			switch (action.payload.type)
			{
				case ChecklistFormType.FORM:
					assert(state.checklist[action.payload.stepId].items[action.payload.item].type === ChecklistType.MULTI_OPTION ||
						state.checklist[action.payload.stepId].items[action.payload.item].type === ChecklistType.MULTI_OPTION_SELECT
					);

					const optionArray = [...state.checklist[action.payload.stepId].items[action.payload.item].options];

					if (action.payload.description || action.payload.description === "")
					{
						optionArray[action.payload.option].description = action.payload.description;
					}

					if (action.payload.next || action.payload.next === "")
					{
						state.checklist[action.payload.stepId].next = action.payload.next;
					}

					state.checklist[action.payload.stepId].items[action.payload.item].options = optionArray;
					return;
				case ChecklistFormType.QUIZ:
					assert(state.checklist[action.payload.stepId].questions[action.payload.item].type === ChecklistType.MULTI_OPTION ||
						state.checklist[action.payload.stepId].questions[action.payload.item].type === ChecklistType.MULTI_OPTION_SELECT ||
						state.checklist[action.payload.stepId].questions[action.payload.item].type === ChecklistType.MULTI_OPTION_3D ||
						state.checklist[action.payload.stepId].questions[action.payload.item].type === ChecklistType.MULTI_OPTION_SELECT_3D
					);

					const optionArrayQuiz = [...state.checklist[action.payload.stepId].questions[action.payload.item].options];

					if (action.payload?.description || action.payload?.description === "")
					{
						optionArrayQuiz[action.payload.option].description = action.payload.description;
					}

					if (action.payload?.next || action.payload?.next === "")
					{
						state.checklist[action.payload.stepId].next = action.payload.next;
					}

					state.checklist[action.payload?.stepId].questions[action.payload?.item].options = optionArrayQuiz;
					return;
			}
		},
		moveOption(state, action)
		{
			switch (action.payload.type)
			{
				case ChecklistFormType.FORM:
					const newArray = state.checklist[action.payload.stepId].items[action.payload.item].options.filter((option, idx) => idx !== action.payload.option);
					newArray.splice(action.payload.index, 0, { ...state.checklist[action.payload.stepId].items[action.payload.item].options[action.payload.option] });

					state.checklist[action.payload.stepId].items[action.payload.item].options = newArray;
					return;
				case ChecklistFormType.QUIZ:
					const newArrayQuiz = state.checklist[action.payload.stepId].questions[action.payload.item].options.filter((option, idx) => idx !== action.payload.option);
					newArrayQuiz.splice(action.payload.index, 0, { ...state.checklist[action.payload.stepId].questions[action.payload.item].options[action.payload.option] });

					state.checklist[action.payload.stepId].questions[action.payload.item].options = newArrayQuiz;
					return;
			}
		},
		addOption(state, action)
		{
			switch (action.payload.type)
			{
				case ChecklistFormType.FORM:
					assert(state.checklist[action.payload.stepId].items[action.payload.item].type === ChecklistType.MULTI_OPTION ||
						state.checklist[action.payload.stepId].items[action.payload.item].type === ChecklistType.MULTI_OPTION_SELECT
					);
					assert(state.checklist[action.payload.stepId].items[action.payload.item].options.length < ChecklistItemMaxOptions);
					state.checklist[action.payload.stepId].items[action.payload.item].options.push(
						action.payload.optionData ?? {
							description: generateNextOptionName(state.checklist[action.payload.stepId].items[action.payload.item].options),
							id: uniqueIdGenerator.next().value
						}
					);
					return;
				case ChecklistFormType.QUIZ:
					assert(state.checklist[action.payload.stepId].questions[action.payload.item].type === ChecklistType.MULTI_OPTION ||
							state.checklist[action.payload.stepId].questions[action.payload.item].type === ChecklistType.MULTI_OPTION_SELECT ||
							state.checklist[action.payload.stepId].questions[action.payload.item].type === ChecklistType.MULTI_OPTION_3D ||
							state.checklist[action.payload.stepId].questions[action.payload.item].type === ChecklistType.MULTI_OPTION_SELECT_3D
					);
					assert(state.checklist[action.payload.stepId].questions[action.payload.item].options.length < ChecklistItemMaxOptions);
					state.checklist[action.payload.stepId].questions[action.payload.item].options.push(
						action.payload.optionData ?? {
							description: generateNextOptionName(state.checklist[action.payload.stepId].questions[action.payload.item].options),
							id: uniqueIdGenerator.next().value,
							correct: false,
							object: null,
						}
					);
					return;
			}
		},
		removeOption(state, action)
		{
			switch (action.payload.type)
			{
				case ChecklistFormType.FORM:
					assert(state.checklist[action.payload.stepId].items[action.payload.item].type === ChecklistType.MULTI_OPTION ||
						state.checklist[action.payload.stepId].items[action.payload.item].type === ChecklistType.MULTI_OPTION_SELECT
					);
					assert(state.checklist[action.payload.stepId].items[action.payload.item].options.length > 0);
					state.checklist[action.payload.stepId].items[action.payload.item].options.splice(action.payload.option, 1);
					return;
				case ChecklistFormType.QUIZ:
					assert(state.checklist[action.payload.stepId].questions[action.payload.item].type === ChecklistType.MULTI_OPTION ||
						state.checklist[action.payload.stepId].questions[action.payload.item].type === ChecklistType.MULTI_OPTION_SELECT ||
						state.checklist[action.payload.stepId].questions[action.payload.item].type === ChecklistType.MULTI_OPTION_3D ||
						state.checklist[action.payload.stepId].questions[action.payload.item].type === ChecklistType.MULTI_OPTION_SELECT_3D
					);
					assert(state.checklist[action.payload.stepId].questions[action.payload.item].options.length > 0);
					state.checklist[action.payload.stepId].questions[action.payload.item].options.splice(action.payload.option, 1);
					return;
			}
		},
		setPreviewProperties(state, action)
		{
			state.simulationEvent = action.payload.simulationEvent ? action.payload.simulationEvent : action.payload.simulationEvent === "" ? "" : action.payload.simulationEvent === 0 ? 0 : state.simulationEvent;
			state.preview = action.payload.preview ? action.payload.preview : action.payload.preview === false ? false : state.preview;
			state.nextStep = action.payload.next ? action.payload.next : action.payload.next === "" ? "" : state.nextStep;
		},
		setFromCopy(state, action)
		{
			const { stepId, content } = action.payload;

			if(!content || Object.keys(content).length <= 0)
			{
				delete state.checklist[stepId];
				return;
			}

			state.checklist[stepId] = { ...content };
		},
		setQuizSettings(state, action)
		{
			state.quizSettings = action.payload ?? {};
		},
		setAnswers(state, action: PayloadAction<{ step: string, answers: Array<QuizAnswerValue> }>)
		{
			if (!state.answers[action.payload.step])
			{
				state.answers[action.payload.step] = [];
			}

			state.answers[action.payload.step] = [...action.payload.answers];
		},
		setRemainingAttempts(state, action)
		{
			if (!state.attempts[action.payload.step])
			{
				state.attempts[action.payload.step] = 0;
			}

			state.attempts[action.payload.step] = action.payload.attempts;
		},
		clearAnswers(state, action: PayloadAction<undefined>)
		{
			state.answers = {};
			state.attempts = {};
			state.questionStatus = QuizQuestionStatus.UNANSWERED;
		},
		setQuestionStatus(state, action)
		{
			state.questionStatus = action.payload;
		},
		setQuestionRequiresCorrectAnswer(state, action)
		{
			const { stepId, item, require } = action.payload;

			state.checklist[stepId].questions[item].requireCorrectAnswer = require;
		},
		copyChecklist(state, action)
		{
			state.clipboard = {...action.payload};
		},
		setCurrentNumberOfRetries(state, action)
		{
			state.currentNumberOfRetries = action.payload;
		},
	},
});

export default ChecklistSlice;
